import Avatar from 'react-avatar';
import {
  RiArrowRightSLine,
  RiArrowUpDownLine,
  RiBuilding2Line,
  RiGlobalLine,
  RiMailLine,
  RiMapPinLine,
  RiPhoneLine,
  RiUserAddLine,
  RiWhatsappLine,
} from 'react-icons/ri';

import { qaVenueBanner } from '../assets';
import { getFieldTypeLabel } from '../constants';
import BottomSheet from '../components/BottomSheet';
import BkArenaModal from './modals/BkArenaModal';
import { truncateString } from '../utils';

// api
const API_URL = String(process.env.REACT_APP_BASE_API_URL || '').replace(/\/+$/, '');

// brand
const NAVY = '#174594';
const SKY = '#5798D1';
const INK = '#12233F';
const MUTED = '#6B7A90';
const TILE = '#E4ECF8';
const LINE = '#E5EAF2';
const PAGE = '#E9EEF6';
const BRAND_NAME = 'QA Venue Solutions';
const LEGACY_COMPANY = 'BK Arena';

// type
const FONT = "'GT Walsheim', 'GT-Walsheim-Regular', sans-serif";
const FONT_BOLD = "'GT-Walsheim-Bold', 'GT Walsheim', sans-serif";
const regular = { fontFamily: FONT, fontWeight: 400 };
const bold = { fontFamily: FONT_BOLD, fontWeight: 700 };

// ---------- sanitizers (all values come from a remote API) ----------

const asText = value => (typeof value === 'string' ? value.trim() : '');

// Only http(s) URLs may be used as clickable links. Schemeless hosts get https://.
const safeHttpUrl = value => {
  const text = asText(value);
  if (!text || /\s/.test(text) || [...text].some(ch => ch.charCodeAt(0) < 32)) return '';
  const candidate = /^[a-z][a-z0-9+.-]*:/i.test(text) ? text : `https://${text}`;
  try {
    const url = new URL(candidate);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : '';
  } catch {
    return '';
  }
};

// Only http(s) or inline image data may be used as an avatar source.
const safeImageUrl = value => {
  const text = asText(value);
  if (!text) return '';
  if (/^data:image\/(png|jpe?g|gif|webp|svg\+xml);base64,[a-z0-9+/=]+$/i.test(text)) return text;
  return safeHttpUrl(text);
};

const safeTel = value => {
  const digits = asText(value).replace(/[^0-9+]/g, '');
  return digits.length >= 3 ? `tel:${digits}` : '';
};

const safeWhatsapp = value => {
  const digits = asText(value).replace(/[^0-9]/g, '');
  return digits.length >= 6 ? `https://wa.me/${digits}` : '';
};

const safeMailto = value => {
  const text = asText(value);
  return /^[^\s@<>"']+@[^\s@<>"']+\.[^\s@<>"']+$/.test(text) ? `mailto:${encodeURIComponent(text)}` : '';
};

const safeMap = value => {
  const text = asText(value);
  return text ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(text)}` : '';
};

const asList = value => (Array.isArray(value) ? value.filter(item => item && asText(item.value)) : []);

const isExternal = href => /^https?:/i.test(href);

// ---------- pieces ----------

// QA Venue Solutions mark (inline SVG so no extra asset is needed)
const QaMark = ({ height = 36, q = NAVY, a = SKY, className = '' }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    viewBox='-10 -10 500 280'
    role='img'
    aria-label={BRAND_NAME}
    height={height}
    className={className}
    style={{ width: 'auto', display: 'block' }}
  >
    <g strokeWidth='3'>
      <path
        fill={a}
        stroke={a}
        d='M269.173,226.091c20.814,18.692,46.567,29.288,76.712,31.53c3.39,0.253,6.801,0.381,10.139,0.381c28.651-0.001,55.582-9.177,82.329-28.054c0.258-.182,0.562-0.274,0.865-0.274c0.236,0,0.473,0.056,0.69,0.168c0.497,0.258,0.81,0.771,0.81,1.332v26.07h44.672c0.034-0.55,0.068-1.082,0.103-1.6c0.125-1.905,0.233-3.55,0.242-5.207c0.047-10.3,0.142-20.772,0.233-30.9c0.238-26.442,0.485-53.785-0.091-80.648c-0.317-14.806-1.349-30.338-6.688-43.622C456.95,39.959,413.097,9.5,355.708,9.5c-6.378,0-12.986,0.38-19.642,1.131c-28.022,3.16-52.41,14.957-74.316,35.995c27.991-12.951,52.373-19.26,74.326-19.26c29.897,0,55.073,11.808,76.968,36.097c31.825,35.307,36.46,85.454,11.533,124.786c-17.378,27.42-42.755,44.458-75.425,50.642c-7.379,1.396-14.813,2.104-22.098,2.104c-0.002,0-0.003,0-0.004,0C307.476,240.994,288.445,236.105,269.173,226.091z'
        transform='translate(-17.5 -17.5)'
      />
      <path
        fill={a}
        stroke={a}
        d='M412.193,188.563c-32.106,52.902-114.683,59.3-161.371,12.211c24.184-44.233,23.342-89.069,0.104-133.767c38.379-47.135,123.469-42.84,159.419,8.908c-21.096-14.708-43.826-23.005-69.721-18.41c-25.922,4.601-46.018,17.825-58.556,41.246c-17.141,32.016-9.122,69.241,19.371,93.274C328.04,214.461,375.741,220.409,412.193,188.563'
        transform='translate(-17.5 -17.5)'
      />
      <path
        fill={q}
        stroke={q}
        d='M117.139,17.757c-26.101,0-51.293,10.337-69.119,28.36c-30.432,30.771-37.44,85.674-15.303,119.891c18.738,28.966,50.547,46.959,83.013,46.958c5.154,0,10.246-0.453,15.23-1.352c-18.26-1.512-35.051-8.5-47.901-20.056c-14.686-13.208-23.656-31.899-25.259-52.63c-1.538-19.894,4.276-39.271,16.372-54.563c12.131-15.339,29.799-25.552,49.747-28.759c4.381-0.705,8.759-1.062,13.012-1.062c17.872,0,34.427,6.186,49.208,18.387c15.829,13.065,24.958,30.234,27.771,52.312c6.399-30.639-13.108-72.675-44.305-92.411C154.013,22.97,135.871,17.757,117.139,17.757z'
        transform='translate(-17.5 -17.5)'
      />
      <path
        fill={q}
        stroke={q}
        d='M201.541,241.101c-0.281-0.247-0.635-0.374-0.992-0.374c-0.263,0-0.527,0.069-0.764,0.209c-18.696,11.083-40.092,16.941-61.874,16.941c-19.347,0-38.521-4.442-56.99-13.203c-40.923-19.411-70.216-65.599-68.2-105.714c4.848,13.604,11.498,26.233,19.816,37.624c11.568,17.471,27.878,31.021,47.126,39.127c12.895,5.809,26.328,8.761,39.891,8.761c7.104,0,14.213-0.831,21.128-2.468c0.818-0.155,1.693-0.332,2.569-0.523l0.551-0.139c0.25-0.062,0.5-0.124,0.748-0.194c48.136-11.105,81.806-53.423,81.806-102.891c0-16.078-3.629-31.655-10.787-46.297c-13.726-31.815-41.146-52.645-81.54-61.94c2.816-0.315,5.702-0.474,8.637-0.474c37.945,0,80.66,26.113,101.568,62.091c25.949,44.654,21.458,101.221-11.173,140.759c-0.516,0.625-0.442,1.545,0.166,2.081l28.196,24.819l-27.04,30.718L201.541,241.101z'
        transform='translate(-17.5 -17.5)'
      />
    </g>
  </svg>
);

// Renders a link only when the href passed sanitizing; otherwise a plain block.
const SafeLink = ({ href, className, children, ...rest }) => {
  if (!href) {
    return <div className={className}>{children}</div>;
  }
  const external = isExternal(href) ? { target: '_blank', rel: 'noopener noreferrer' } : {};
  return (
    <a href={href} className={className} {...external} {...rest}>
      {children}
    </a>
  );
};

// one contact card
const InfoCard = ({ href, Icon, label, value, trailing }) => (
  <div className='flex items-center gap-4 bg-white rounded-[22px] border p-[14px]' style={{ borderColor: LINE }}>
    <SafeLink href={href} className='flex items-center gap-4 min-w-0 flex-1 text-left'>
      <div
        className='flex-shrink-0 w-[54px] h-[54px] rounded-[16px] flex items-center justify-center'
        style={{ backgroundColor: TILE }}
      >
        <Icon size={24} style={{ color: NAVY }} aria-hidden='true' />
      </div>
      <div className='min-w-0'>
        <p className='text-[13px] leading-5' style={{ ...bold, color: INK }}>
          {label}
        </p>
        <p className='text-[14px] leading-5 truncate' style={{ ...regular, color: MUTED }}>
          {value}
        </p>
      </div>
    </SafeLink>
    {trailing}
  </div>
);

const Chevron = ({ href, label }) => (
  <SafeLink href={href} className='flex-shrink-0 pr-1' aria-label={label}>
    <RiArrowRightSLine size={24} style={{ color: '#9AA6B8' }} aria-hidden='true' />
  </SafeLink>
);

// ---------- page ----------

const QaSolutionsProfile = ({ profile, toggle, modal, setModal, id, isLoading }) => {
  const info = profile?.data?.tag_info;

  const firstName = asText(info?.fname);
  const lastName = asText(info?.lname);
  const fullName = [asText(info?.title), firstName, lastName].filter(Boolean).join(' ');
  const position = asText(info?.position);
  const companyRaw = asText(info?.company);
  const companyName = companyRaw === LEGACY_COMPANY ? BRAND_NAME : companyRaw;
  const avatarUrl = safeImageUrl(info?.avatar);
  const vcardUrl = API_URL && id ? `${API_URL}/api/v1/cards/vcard/${encodeURIComponent(String(id))}` : '';

  const phones = asList(info?.phones);
  const emails = asList(info?.emails);
  const websites = asList(info?.websites);
  const addresses = asList(info?.addresses);

  return (
    <>
      {info && (
        <div className='min-h-screen pb-6 py-xl-6 px-l-4' style={{ ...regular, backgroundColor: PAGE }}>
          <div
            className='mx-auto max-w-[440px] bg-white rounded-xl-[36px] p-[14px] pb-6 text-center'
            style={{ boxShadow: '0 20px 60px rgba(23,69,148,0.10)' }}
          >
            {/* Hero: venue background + navy overlay + logo lockup */}
            <div className='relative rounded-[26px] overflow-hidden' style={{ aspectRatio: '1.55 / 1' }}>
              <img
                src={qaVenueBanner}
                alt=''
                aria-hidden='true'
                className='absolute inset-0 w-full h-full object-cover object-center'
              />
              <div className='absolute inset-0' style={{ backgroundColor: 'rgba(23,69,148,0.72)' }} />

              <div className='absolute inset-0 flex items-center justify-center pb-10'>
                <div className='flex items-center gap-3'>
                  <QaMark height={64} q='#FFFFFF' a='#8DB9E6' />
                  <div className='text-left leading-none'>
                    <p className='text-white text-[20px] tracking-[0.12em] uppercase' style={bold}>
                      QA Venue
                    </p>
                    <p
                      className='text-[16px] tracking-[0.28em] uppercase mt-[2px]'
                      style={{ ...regular, color: '#8DB9E6' }}
                    >
                      Solutions
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Avatar straddling the hero edge */}
            <div className='relative -mt-[70px] flex justify-center'>
              <div className='rounded-full bg-white p-[7px]' style={{ boxShadow: '0 8px 24px rgba(18,35,63,0.12)' }}>
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={fullName || 'Profile photo'}
                    referrerPolicy='no-referrer'
                    className='w-[134px] h-[134px] object-cover object-top rounded-full'
                  />
                ) : (
                  <Avatar
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', ...bold }}
                    name={`${firstName} ${lastName}`.trim() || BRAND_NAME}
                    round
                    color={NAVY}
                    fgColor='#fff'
                    size={134}
                  />
                )}
              </div>
            </div>

            {/* Names */}
            <div className='px-4 pt-4'>
              <h1 className='text-[15px] leading-6' style={{ ...bold, color: INK }}>
                {fullName}
              </h1>
              {position && (
                <p
                  className='mt-1 text-[12px] leading-5 tracking-[0.16em] uppercase'
                  style={{ ...regular, color: MUTED }}
                >
                  {position}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className='px-2 mt-6 flex items-center gap-3'>
              <button
                type='button'
                onClick={toggle}
                className='flex-1 flex items-center justify-center gap-2 h-[54px] rounded-[18px] border-2 bg-white text-[15px]'
                style={{ ...bold, borderColor: NAVY, color: NAVY }}
              >
                <RiArrowUpDownLine size={20} aria-hidden='true' />
                Exchange
              </button>

              {vcardUrl && (
                <a
                  href={vcardUrl}
                  className='flex-1 flex items-center justify-center gap-2 h-[54px] rounded-[18px] text-white text-[15px]'
                  style={{ ...bold, backgroundColor: NAVY, boxShadow: '0 10px 24px rgba(23,69,148,0.28)' }}
                >
                  <RiUserAddLine size={20} aria-hidden='true' />
                  Save Contact
                </a>
              )}
            </div>

            {/* Personal info */}
            <div className='px-2 mt-8'>
              <div className='flex items-center gap-4 mb-5'>
                <span className='h-px flex-1' style={{ backgroundColor: LINE }} />
                <h2 className='text-[12px] leading-5 tracking-[0.22em] uppercase' style={{ ...bold, color: INK }}>
                  Personal Info
                </h2>
                <span className='h-px flex-1' style={{ backgroundColor: LINE }} />
              </div>

              <div className='space-y-4'>
                {companyName && <InfoCard Icon={RiBuilding2Line} label='Company' value={companyName} />}

                {phones.map(item => {
                  const value = asText(item.value);
                  const wa = safeWhatsapp(value);
                  return (
                    <InfoCard
                      key={`phone-${value}`}
                      href={safeTel(value)}
                      Icon={RiPhoneLine}
                      label={`Phone (${getFieldTypeLabel(item.type) || 'Other'})`}
                      value={value}
                      trailing={
                        wa ? (
                          <SafeLink
                            href={wa}
                            className='flex-shrink-0 w-[50px] h-[50px] rounded-[16px] flex items-center justify-center text-white'
                            style={{ backgroundColor: '#5DD66C' }}
                            aria-label={`WhatsApp ${value}`}
                          >
                            <RiWhatsappLine size={26} aria-hidden='true' />
                          </SafeLink>
                        ) : null
                      }
                    />
                  );
                })}

                {emails.map(item => {
                  const value = asText(item.value);
                  const href = safeMailto(value);
                  return (
                    <InfoCard
                      key={`email-${value}`}
                      href={href}
                      Icon={RiMailLine}
                      label={`Email (${getFieldTypeLabel(item.type) || 'Other'})`}
                      value={value}
                      trailing={href ? <Chevron href={href} label={`Email ${value}`} /> : null}
                    />
                  );
                })}

                {websites.map(item => {
                  const value = asText(item.value);
                  const href = safeHttpUrl(value);
                  return (
                    <InfoCard
                      key={`web-${value}`}
                      href={href}
                      Icon={RiGlobalLine}
                      label={`Website (${getFieldTypeLabel(item.type) || 'Other'})`}
                      value={truncateString(value, 35)}
                      trailing={href ? <Chevron href={href} label={`Open ${value}`} /> : null}
                    />
                  );
                })}

                {addresses.map(item => {
                  const value = asText(item.value);
                  const href = safeMap(value);
                  return (
                    <InfoCard
                      key={`address-${value}`}
                      href={href}
                      Icon={RiMapPinLine}
                      label='Location'
                      value={value}
                      trailing={<Chevron href={href} label={`Open ${value} on the map`} />}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {info && (
        <BottomSheet isOpen={modal} onChange={toggle} tagInfo={info}>
          <BkArenaModal info={info} setModal={setModal} tagId={profile?.data?.id} />
        </BottomSheet>
      )}

      {!info && !isLoading && (
        <div className='mx-auto max-w-[560px] text-center pt-[45vh] px-6' style={regular}>
          <QaMark height={40} className='mx-auto mb-4' />
          <h3 className='text-[15px] leading-6' style={{ ...bold, color: NAVY }}>
            We couldn't find that profile
          </h3>
          <p className='pt-2 text-[12px] leading-5 text-red-900'>
            This might be due to poor network or that profile doesn't exist
          </p>
        </div>
      )}
    </>
  );
};

export default QaSolutionsProfile;
